import React, { useState, useEffect } from "react";
import { ChevronDown, LogOut, User, HelpCircle } from "lucide-react";
import AdaptLogo from "../../images/Adapt_Logo.jpg";

const Header = ({ user, onLogout, showUserDropdown = false, title = "Adapt Authoring Tool" }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [helpUrl, setHelpUrl] = useState('');

    // Load help URL from config.json
    useEffect(() => {
        fetch('/config.json', { cache: 'no-store' })
            .then(response => response.json())
            .then(config => {
                if (config.helpUrl) {
                    setHelpUrl(config.helpUrl);
                }
            })
            .catch(error => console.error('Error loading help URL from config:', error));
    }, []);

    const handleLogout = () => {
        setIsDropdownOpen(false);
        if (onLogout) {
            onLogout();
        }
    };

    const handleBackdropClick = () => {
        setIsDropdownOpen(false);
    };

    const handleBackdropKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsDropdownOpen(false);
        }
    };

    // Get display name from user object
    const getDisplayName = () => {
        if (typeof user === 'string') return user;
        if (user?.name) return user.name;
        if (user?.displayName) return user.displayName;
        if (user?.givenName && user?.surname) return `${user.givenName} ${user.surname}`;
        if (user?.email) return user.email.split('@')[0];
        return 'User';
    };

    // Get user role
    const getUserRole = () => {
        if (user?.jobTitle) return user.jobTitle;
        if (user?.role) return user.role;
        return 'Admin';
    };

    return (
        <div className="w-full flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            {/* Left side - Logo and title */}
            <div className="flex items-center">
                <img src={AdaptLogo} alt="Adapt Logo" className="w-10 h-10 mr-3 object-contain" />
                <span className="text-xl font-semibold text-gray-700">{title}</span>
            </div>

            {/* Right side - Help link and User dropdown */}
            <div className="flex items-center gap-4">
                {/* Help link (only show when user is logged in) */}
                {showUserDropdown && helpUrl && (
                    <a
                        href={helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 text-brand-blue hover:text-brand-darkBlue hover:bg-gray-50 rounded-lg transition-colors font-medium"
                        aria-label="Open Help documentation in a new tab"
                    >
                        <HelpCircle className="w-5 h-5" />
                        <span className="hidden sm:inline">Help</span>
                    </a>
                )}

                {/* User dropdown (only show if showUserDropdown is true) */}
                {showUserDropdown && (
                    <div className="relative">
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                        {/* User Avatar */}
                        <div className="w-8 h-8 bg-brand-blue rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                        </div>

                        {/* User Info */}
                        <div className="text-left hidden sm:block">
                            <div className="text-sm font-medium text-gray-900">{getDisplayName()}</div>
                        </div>

                        {/* Dropdown arrow */}
                        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown menu */}
                    {isDropdownOpen && (
                        <>
                            {/* Backdrop */}
                            <button
                                type="button"
                                className="fixed inset-0 z-10 bg-transparent cursor-default"
                                onClick={handleBackdropClick}
                                onKeyDown={handleBackdropKeyDown}
                                aria-label="Close dropdown"
                            />

                            {/* Dropdown content */}
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                                <div className="py-1">
                                    {/* User info section */}
                                    <div className="px-4 py-2 border-b border-gray-100">
                                        <div className="text-sm font-medium text-gray-900">{getDisplayName()}</div>
                                    </div>

                                    {/* Menu items */}
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
            </div>
        </div>
    );
};

export default Header;
